import React from 'react'
import { Paper, Box, Typography, Stack, FormControlLabel, Switch } from '@mui/material'

const FeatureToggles = ({
  print,
  QRSearch,
  IDSearch,
  uploadFace,
  isFaceCheckin,
  IDFace,
  selfCheckin,
  isShirtImage,
  autoPickup,
  onPrintChange,
  onQRSearchChange,
  onIDSearchChange,
  onUploadFaceChange,
  onIsFaceCheckinChange,
  onIDFaceChange,
  onSelfCheckinChange,
  onIsShirtImageChange,
  onAutoPickupChange,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        mb: { xs: 1.5, sm: 2, md: 2.5 },
        backgroundColor: 'background.paper',
        border: (theme) =>
          `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
        boxShadow: (theme) =>
          theme.palette.mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'
            : '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)',
      }}>
      <Box sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.25, sm: 0.375, md: 0.5 },
            color: 'text.primary',
            fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
          }}>
          Event Features
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
          }}>
          Configure features and options for your event
        </Typography>
      </Box>

      <Stack direction="column" spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        {/* Top Row: Print, Search Modes, Face Detection */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{ width: '100%' }}>
          {/* Print */}
          <Box
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '1 1 calc(33.333% - 1.33rem)',
                md: '1 1 calc(33.333% - 1.67rem)',
              },
              p: { xs: 1.25, sm: 1.5, md: 2 },
              borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.02)'
                  : 'rgba(255, 255, 255, 0.03)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)'}`,
            }}>
            <Stack direction="column" spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  lineHeight: 1.3,
                }}>
                Print
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={print}
                    onChange={onPrintChange}
                    size="small"
                    sx={{
                      '& .MuiSwitch-thumb': {
                        width: { xs: 16, sm: 18, md: 20 },
                        height: { xs: 16, sm: 18, md: 20 },
                      },
                    }}
                  />
                }
                label="Print"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    color: 'text.primary',
                    fontWeight: 500,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Search Modes */}
          <Box
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '1 1 calc(33.333% - 1.33rem)',
                md: '1 1 calc(33.333% - 1.67rem)',
              },
              p: { xs: 1.25, sm: 1.5, md: 2 },
              borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.02)'
                  : 'rgba(255, 255, 255, 0.03)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)'}`,
            }}>
            <Stack direction="column" spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  lineHeight: 1.3,
                }}>
                Search Modes
              </Typography>
              <Stack spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={QRSearch}
                      onChange={onQRSearchChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="QR Code"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={IDSearch}
                      onChange={onIDSearchChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="ID Card"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Face Detection */}
          <Box
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '1 1 calc(33.333% - 1.33rem)',
                md: '1 1 calc(33.333% - 1.67rem)',
              },
              p: { xs: 1.25, sm: 1.5, md: 2 },
              borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.02)'
                  : 'rgba(255, 255, 255, 0.03)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)'}`,
            }}>
            <Stack direction="column" spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  lineHeight: 1.3,
                }}>
                Face Detection
              </Typography>
              <Stack spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={uploadFace}
                      onChange={onUploadFaceChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="From Upload"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isFaceCheckin}
                      onChange={onIsFaceCheckinChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="FaceCheckin"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
                {IDSearch && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={IDFace}
                        onChange={onIDFaceChange}
                        size="small"
                        sx={{
                          '& .MuiSwitch-thumb': {
                            width: { xs: 16, sm: 18, md: 20 },
                            height: { xs: 16, sm: 18, md: 20 },
                          },
                        }}
                      />
                    }
                    label="ID Card"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        fontWeight: 500,
                      },
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {/* Bottom Row: Self-Checkin, Others */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{ width: '100%' }}>
          {/* Self-Checkin */}
          <Box
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '1 1 calc(50% - 1rem)',
                md: '1 1 calc(50% - 1.25rem)',
              },
              p: { xs: 1.25, sm: 1.5, md: 2 },
              borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.02)'
                  : 'rgba(255, 255, 255, 0.03)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)'}`,
            }}>
            <Stack direction="column" spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  lineHeight: 1.3,
                }}>
                Self-Checkin
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={selfCheckin}
                    onChange={onSelfCheckinChange}
                    size="small"
                    sx={{
                      '& .MuiSwitch-thumb': {
                        width: { xs: 16, sm: 18, md: 20 },
                        height: { xs: 16, sm: 18, md: 20 },
                      },
                    }}
                  />
                }
                label="Self-Checkin"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    color: 'text.primary',
                    fontWeight: 500,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Others */}
          <Box
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '1 1 calc(50% - 1rem)',
                md: '1 1 calc(50% - 1.25rem)',
              },
              p: { xs: 1.25, sm: 1.5, md: 2 },
              borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.02)'
                  : 'rgba(255, 255, 255, 0.03)',
              border: (theme) =>
                `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)'}`,
            }}>
            <Stack direction="column" spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  lineHeight: 1.3,
                }}>
                Others
              </Typography>
              <Stack spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isShirtImage}
                      onChange={onIsShirtImageChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="Shirt-Image"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoPickup}
                      onChange={onAutoPickupChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          width: { xs: 16, sm: 18, md: 20 },
                          height: { xs: 16, sm: 18, md: 20 },
                        },
                      }}
                    />
                  }
                  label="autoPickup"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                      fontWeight: 500,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default FeatureToggles
